import { useCallback, useContext } from 'react';

import cn from 'classnames';

import { smoothScrollToAnchor } from 'libs/node';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Button } from 'ui/atoms/Button/Button';
import { Icon } from 'ui/atoms/Icon/Icon';

import styles from './AdvantageBlock.module.scss';

const baseClass = 'advantage-block';

type Props = {
  title: string;
  description: string;
  className?: string;
};

export const features = [
  {
    title: 'Data Sourcing & Traceability',
    subtitle: 'The origin and transparency of data.',
    icon: 'data-sourcing',
    global: 'Direct from 200+ official registries.',
    moody: 'Black box sourcing, low transparency.',
  },
  {
    title: 'Licensing & Usage Rights',
    subtitle: 'Freedom to use your purchased data.',
    icon: 'licensing-check',
    global: 'Full commercial rights to build or resell.',
    moody: 'Restrictive "internal use only" licenses.',
  },
  {
    title: 'Pricing & Fees',
    subtitle: 'Predictability of costs over time.',
    icon: 'pricing',
    global: 'Price Lock Guarantee. No surprises.',
    moody: 'Opaque pricing & unpredictable hikes.',
  },
  {
    title: 'Data Freshness',
    subtitle: 'How up-to-date the information is.',
    icon: 'data-info',
    global: 'First-party data with real-time updates.',
    moody: 'Often months old, stale data.',
  },
  {
    title: 'Platform & Technology',
    subtitle: 'Legacy platforms; slow and unintuitive.',
    icon: 'platform-tehnology',
    global: 'Modern, fast platform for 2025 workflows.',
    moody: 'Legacy platforms; slow and unintuitive.',
  },
  {
    title: 'Customer Support',
    subtitle: 'Quality of help when you need it.',
    icon: 'support',
    global: 'Your strategic partner. Fast, direct answers.',
    moody: 'Slow, bureaucratic, multi-layered support.',
  },
];

export const AdvantageBlock = ({ title, description, className }: Props) => {
  const { internationalization } = useContext(ContentContext);
  const { t } = useInt();

  const onSmoothScrollTo = useCallback((ev: React.MouseEvent) => {
    ev.preventDefault();

    smoothScrollToAnchor('pricing');
  }, []);

  return (
    <section className={cn(styles[`${baseClass}-wrapper`], className)}>
      <div className={styles[`${baseClass}-container`]}>
        {(title || description) && <TitleBlock title={title} description={description} />}

        <div className={styles[`${baseClass}-columns`]}>
          <div className={styles[`${baseClass}-column`]}>
            <div className={styles[`${baseClass}-column-header`]}>{t('Feature')}</div>
            {features.map((f, i) => (
              <div key={i} className={styles[`${baseClass}-column-cell`]}>
                <Icon type={f.icon} size="md" className={styles[`${baseClass}-featureIcon`]} />

                <div className={styles[`${baseClass}-featureContent`]}>
                  <div className={styles[`${baseClass}-featureTitle`]}>{f.title}</div>
                  <div className={styles[`${baseClass}-featureSubtitle`]}>{f.subtitle}</div>
                </div>
              </div>
            ))}
          </div>

          <div className={cn(styles[`${baseClass}-column`], styles[`${baseClass}-column-highlighted`])}>
            <div className={styles[`${baseClass}-column-header`]}>Global Database</div>

            {features.map((f, i) => (
              <div key={i} className={styles[`${baseClass}-column-cell`]}>
                <span className={styles[`${baseClass}-iconPositive`]}>
                  <Icon type="check-1" />
                </span>

                {f.global}
              </div>
            ))}
          </div>

          <div className={styles[`${baseClass}-column`]}>
            <div className={styles[`${baseClass}-column-header`]}>{internationalization['target_short']}</div>

            {features.map((f, i) => (
              <div key={i} className={styles[`${baseClass}-column-cell`]}>
                <span className={styles[`${baseClass}-iconNegative`]}>
                  <Icon type="close-1" />
                </span>

                {f.moody}
              </div>
            ))}
          </div>
        </div>

        <a href="#pricing" onClick={onSmoothScrollTo}>
          <Button type="blue" size="xl">
            Discover the Difference
          </Button>
        </a>
      </div>
    </section>
  );
};
